import { type FC, useCallback, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Card } from 'react-bootstrap'
import Chart from 'react-apexcharts'
import { type ApexOptions } from 'apexcharts'
import { type UserScorecardHistoryPoint } from '../../../shared/insights/store/insights'
import { SCORE_BENCHMARK } from '../scoreRoster'
import { type RangeKey } from '../userScoreRange'
import { formatAxisDay, formatMonthCategory, formatMonthHeading, formatWeekRange, toMonthlyPoints, toWeeklyPoints, toWeeklySeries } from './scoreHistoryUtils'
import styles from './UserScoreHistoryCard.module.scss'

interface UserScoreHistoryCardProps {
  scoreHistory: UserScorecardHistoryPoint[]
  range: RangeKey
}

interface TooltipState {
  left: number
  top: number
  heading: string
  average: number
  meetingCount: number
  min: number
  max: number
}

const AXIS_LABEL_STYLE = { colors: '#495057', fontSize: '12px', fontWeight: 500 }

export const UserScoreHistoryCard: FC<UserScoreHistoryCardProps> = ({ scoreHistory, range }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  const showTooltip = useCallback((marker: Element, content: Omit<TooltipState, 'left' | 'top'>) => {
    const container = containerRef.current
    if (!container) {
      return
    }
    const markerRect = marker.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    setTooltip({
      ...content,
      left: markerRect.left - containerRect.left + markerRect.width / 2,
      top: markerRect.top - containerRect.top,
    })
  }, [])

  const hideTooltip = useCallback(() => setTooltip(null), [])

  const chart = useMemo(() => {
    const baseOptions: ApexOptions = {
      chart: {
        type: 'line',
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        fontFamily: 'inherit',
      },
      colors: ['#7749f8'],
      dataLabels: { enabled: false },
      legend: { show: false },
      stroke: { width: 2, curve: 'straight' },
      // Same shape as the legend dot: an 8px solid core with a 4px translucent halo. The marker stroke straddles the
      // circle edge, so it needs double the halo width to land fully outside the core (the inner half repaints the fill).
      markers: { size: 4, strokeWidth: 8, strokeColors: 'rgba(119, 73, 248, 0.2)', hover: { sizeOffset: 0 } },
      fill: { type: 'gradient', gradient: { type: 'vertical', shadeIntensity: 0, opacityFrom: 0.25, opacityTo: 0, stops: [0, 100] } },
      // Side padding leaves room for the halo of the first and last marker, which sit on the plot edges
      grid: { borderColor: '#e9ecef', padding: { left: 8, right: 8 }, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
      annotations: { yaxis: [{ y: SCORE_BENCHMARK, borderColor: '#28a745', strokeDashArray: 5 }] },
      tooltip: { enabled: false },
      xaxis: {
        type: 'datetime',
        labels: { style: AXIS_LABEL_STYLE },
        tooltip: { enabled: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { min: 0, max: 100, tickAmount: 5, labels: { style: AXIS_LABEL_STYLE } },
    }

    if (range === '90d') {
      const weekly = toWeeklyPoints(scoreHistory)
      return {
        series: [{ name: 'Average meeting score', type: 'area' as const, data: toWeeklySeries(weekly) }],
        options: {
          ...baseOptions,
          chart: {
            ...baseOptions.chart,
            events: {
              dataPointMouseEnter: (event: MouseEvent, _chart: unknown, opts: { dataPointIndex: number }) => {
                const point = weekly[opts.dataPointIndex]
                if (point) {
                  showTooltip(event.target as Element, { heading: formatWeekRange(point), ...point })
                }
              },
              dataPointMouseLeave: hideTooltip,
            },
          },
          xaxis: {
            ...baseOptions.xaxis,
            labels: { formatter: (_value: string, timestamp?: number) => formatAxisDay(timestamp as number), style: AXIS_LABEL_STYLE },
          },
        },
      }
    }

    const points = toMonthlyPoints(scoreHistory)
    return {
      series: [{ name: 'Average meeting score', type: 'area' as const, data: points.map((point) => point.average) }],
      options: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          events: {
            dataPointMouseEnter: (event: MouseEvent, _chart: unknown, opts: { dataPointIndex: number }) => {
              const point = points[opts.dataPointIndex]
              if (point) {
                showTooltip(event.target as Element, { heading: formatMonthHeading(point.month), ...point })
              }
            },
            dataPointMouseLeave: hideTooltip,
          },
        },
        // Category axis puts exactly one tick under each monthly point, unlike datetime tick interpolation
        xaxis: {
          type: 'category' as const,
          categories: points.map((point) => formatMonthCategory(point.month)),
          labels: { style: AXIS_LABEL_STYLE },
          tooltip: { enabled: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
      },
    }
  }, [range, scoreHistory, showTooltip, hideTooltip])

  return (
    <Card className='h-100 zocks-border-color-gray-200 zocks-border-0.75'>
      <Card.Body className='d-flex flex-column p-4 zocks-gap-1.5'>
        <h5 className='insights-section-title mb-0'>Meeting score history</h5>
        {scoreHistory.length === 0 ? (
          <div className='flex-grow-1 d-flex align-items-center justify-content-center paragraph-medium zocks-text-gray-600'>Not enough data</div>
        ) : (
          <div className='d-flex flex-column gap-3 flex-grow-1'>
            <div ref={containerRef} className={classNames('flex-grow-1 position-relative', styles.chartArea)} onMouseLeave={hideTooltip}>
              <Chart key={range} options={chart.options} series={chart.series} type='line' height={224} />
              {tooltip ? (
                <div className={styles.chartTooltip} style={{ left: tooltip.left, top: tooltip.top }}>
                  <div className='bold'>{tooltip.heading}</div>
                  <div>
                    Average {tooltip.average} across <span className='bold'>{tooltip.meetingCount}</span> meetings
                  </div>
                  <div>
                    Range {tooltip.min} to {tooltip.max}
                  </div>
                </div>
              ) : null}
            </div>
            <div className='d-flex align-items-center justify-content-center gap-4 paragraph-small zocks-text-gray-700'>
              <div className='d-flex align-items-center gap-2'>
                <span className={styles.legendDot} />
                Average meeting score
              </div>
              <div className='d-flex align-items-center gap-2'>
                <span className={styles.legendBenchmarkSwatch} />
                Benchmark ({SCORE_BENCHMARK})
              </div>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
